/* eslint-disable @typescript-eslint/no-explicit-any */
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { getIn } from 'formik';

import { updateField } from '../../store/customer';
import { clearFieldError } from '../../store/fieldError';
import { ErrorIcon, ErrorValidation } from '../ErrorValidation';


interface FieldTextAreaProps {
  formik: any;
  store: any;
  name: string;
  required?: boolean;
  type?: string;
  label?: string;
  maxLength?: number;
  placeholder?: string;
  hasGroupError?: boolean;
  dispatch: (action: any) => void;
  apiService: any;
  disableAutoSave?: boolean;
}

/* ----------------------------------
   Component
---------------------------------- */

function FieldTextArea({
  formik,
  store,
  name,
  required,
  type,
  label,
  maxLength,
  placeholder,
  hasGroupError,
  dispatch,
  apiService,
  disableAutoSave
}: FieldTextAreaProps): JSX.Element {
  const [isDirty, setFieldDirty] = useState(false);
  const [isTouch, setIsTouch] = useState(false);

  useEffect(() => {
    if (window.location.href.includes('review')) {
      setIsTouch(true);
    }
  }, []);

  const handleOnBlur = () => {
    setIsTouch(true);

    if (isDirty) {
      formik.form.setFieldTouched(name, true).then((error: any) => {
        if (getIn(error, name) === undefined) {
          dispatch(clearFieldError(name));
        }
      });

      !disableAutoSave &&
        !hasError &&
        apiService.updateCustomer(store.customer, dispatch);

      setFieldDirty(false);
    }
  };

  const errorMessage = getIn(formik.form.errors, name);
  const hasError = errorMessage !== undefined;

  const handleOnChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFieldDirty(true);
    const { value } = event.target;

    dispatch(updateField({ name, value }));
    formik.form.setFieldValue(name, value);
  };

  const outlineBorderColor =
    (((hasError && isTouch) || hasGroupError) &&
      'var(--require-border)') ||
    'var(--input-border-normal)';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        width: '100%',
        mb: '16px',
        paddingTop: '4px'
      }}
    >
      <Box
        sx={{
          width: '100%',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: outlineBorderColor
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: outlineBorderColor
          }
        }}
      >
        <TextField
          fullWidth
          multiline
          minRows={3}
          maxRows={3}
          variant="outlined"
          type={type}
          name={name}
          label={label || ''}
          required={!!required}
          margin="none"
          inputProps={{
            maxLength,
            'aria-required': !!required
          }}
          error={(hasError && isTouch) || hasGroupError}
          onBlur={handleOnBlur}
          value={formik.field.value || ''}
          placeholder={placeholder}
          onChange={handleOnChange}
          FormHelperTextProps={{
            sx: { display: 'none' }
          }}
          InputProps={{
            sx: {
              padding: 0,
              '& textarea': {
                padding: '8px',
                resize: 'none',
                overflow: 'auto'
              }
            }
          }}
        />
      </Box>

      {hasError && isTouch && (
        <ErrorValidation>
          <ErrorIcon />
          <p>{errorMessage}</p>
        </ErrorValidation>
      )}
    </Box>
  );
}

export default FieldTextArea;